import type { CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import MuiSwitch from "@mui/material/Switch";

// antd <Switch> -> MUI Switch. antd onChange(checked, event); MUI onChange(event, checked).
// We adapt so call sites keep the antd (checked) signature. checkedChildren/
// unCheckedChildren (inner labels) have no MUI equivalent and are ignored.

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean, event: unknown) => void;
  disabled?: boolean;
  loading?: boolean;
  size?: "small" | "default";
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
  /** antd onClick(checked, event). */
  onClick?: (checked: boolean, event: unknown) => void;
  style?: CSSProperties;
  className?: string;
  id?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

export const Switch = ({
  checked, defaultChecked, onChange, onClick, disabled, loading, size, style, className, id,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  checkedChildren, unCheckedChildren,
  "data-cy": dataCy, "data-testid": dataTestid,
  ...rest
}: SwitchProps) => (
  <MuiSwitch
    checked={checked}
    defaultChecked={defaultChecked}
    disabled={disabled || loading}
    size={size === "small" ? "small" : "medium"}
    onChange={(e, isChecked) => {
      onChange?.(isChecked, e);
      onClick?.(isChecked, e);
    }}
    style={style}
    className={className}
    id={id}
    // Route data-cy/data-testid onto the inner <input> (the real toggle target).
    // On the MUI Switch ROOT span a cypress .click() lands on the track center and
    // does NOT flip the switch (the switchBase/input covers only part of the root) —
    // that left the lookthrough/currency/period/peers switches un-toggled in cypress.
    // The input is the reliable, antd-parity click target.
    inputProps={{ "data-cy": dataCy, "data-testid": dataTestid } as unknown as InputHTMLAttributes<HTMLInputElement>}
    {...rest}
  />
);

export default Switch;
